'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Eye,
  EyeOff,
  Copy,
  Check,
  Shield,
  Activity,
  Layers,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

import { useRouter } from 'next/navigation';

type Tab = 'login' | 'signup';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const MOCK_JWT =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c3ItMDAxIiwiZW1haWwiOiJvcHMuYWRtaW5AbWVkaWFhdWRpdC5pbyIsInJvbGUiOiJhZG1pbiIsImlhdCI6MTc0NTM4NjA2MywiZXhwIjoxNzQ1NDcyNDYzfQ.stub_signature_replace_with_real';

export default function AuthForm() {
  const [tab, setTab] = useState<Tab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const router = useRouter();

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupForm>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
  });

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const onLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      // POST /v1/auth/token — JWT Bearer token for AuditGen Flutter mobile app contract
      const res = await fetch('/api/v1/auth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Authentication failed' }));
        setIsLoading(false);
        loginForm.setError('email', {
          message: err.error ?? 'Invalid credentials',
        });
        return;
      }

      const { access_token } = await res.json();
      // Store JWT for subsequent /v2/* requests
      if (typeof window !== 'undefined') {
        localStorage.setItem('audit_jwt', access_token);
      }
      setIssuedToken(access_token);
      setIsLoading(false);
      toast.success('Authenticated — JWT issued');
      setTimeout(() => router.push('/audit-batch-dashboard'), 2000);
    } catch {
      setIsLoading(false);
      loginForm.setError('email', { message: 'Network error — check connection' });
    }
  };

  const onSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', {
        message: 'Passwords do not match',
      });
      return;
    }
    setIsLoading(true);
    // Backend integration point: POST /v1/auth/register { name, email, password }
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);
    toast.success('Account created — you can now sign in');
    setTab('login');
    loginForm.setValue('email', data.email);
  };

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]">
      {/* Brand Panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] flex-col justify-between bg-[hsl(240_10%_4%)] border-r border-[hsl(var(--border))] p-10 relative overflow-hidden shrink-0">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-[hsl(var(--primary)/0.04)] blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-[200px] h-[200px] rounded-full bg-amber-500/5 blur-2xl" />
        </div>

        <div className="relative">
          <div className="flex items-center gap-2.5 mb-12">
            <AppLogo size={36} />
            <span className="font-700 text-lg tracking-tight text-[hsl(var(--foreground))]">
              Auditgen.tech
            </span>
          </div>
          <h1 className="text-4xl font-700 leading-tight text-[hsl(var(--foreground))] mb-4">
            Enterprise AI
            <br />
            <span className="text-[hsl(var(--primary))]">Media Compliance</span>
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] text-base leading-relaxed">
            Bulk-audit media files at 1,000/hr. AI risk scoring, content flag
            detection, and real-time webhook delivery for your mobile pipeline.
          </p>
        </div>

        <div className="relative space-y-4">
          {[
            {
              icon: Shield,
              title: 'AI Risk Scoring',
              desc: 'sha3-512 integrity + LME risk analysis on every file',
            },
            {
              icon: Layers,
              title: 'BullMQ Job Queue',
              desc: '20 concurrent workers, 3× exponential retry on failure',
            },
            {
              icon: Activity,
              title: 'Real-time Monitoring',
              desc: 'Queue depth, worker utilization, p99 latency tracking',
            },
          ].map((feat) => {
            const FIcon = feat.icon;
            return (
              <div
                key={`feat-${feat.title}`}
                className="flex items-start gap-3 p-3 rounded-xl bg-[hsl(var(--muted)/0.3)] border border-[hsl(var(--border))]"
              >
                <span className="p-2 rounded-lg bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] shrink-0">
                  <FIcon size={14} />
                </span>
                <div>
                  <p className="text-sm font-600 text-[hsl(var(--foreground))]">
                    {feat.title}
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    {feat.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <p className="relative text-xs text-[hsl(var(--muted-foreground))]">
          © 2026 Auditgen.tech Platform · Enterprise Edition
        </p>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={28} />
            <span className="font-700 text-base tracking-tight">Auditgen.tech</span>
          </div>

          {/* Token success state */}
          {issuedToken ? (
            <div className="animate-slide-up">
              <div className="flex items-center gap-2 mb-2">
                <Check size={16} className="text-emerald-400" />
                <p className="text-sm font-600 text-emerald-400">
                  JWT Issued — Redirecting to Dashboard
                </p>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                Store this token securely. It expires in 24 hours.
              </p>
              <div className="relative bg-[hsl(var(--muted))] rounded-lg p-4 border border-[hsl(var(--border))]">
                <p className="font-mono text-[10px] text-[hsl(var(--primary))] break-all leading-relaxed pr-8">
                  {issuedToken}
                </p>
                <button
                  onClick={() => copyToClipboard(issuedToken, 'jwt')}
                  className="absolute top-3 right-3 p-1.5 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))] transition-colors"
                  aria-label="Copy JWT token"
                >
                  {copiedField === 'jwt' ? (
                    <Check size={13} className="text-emerald-400" />
                  ) : (
                    <Copy size={13} />
                  )}
                </button>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                <Loader2 size={12} className="animate-spin" />
                Redirecting to Audit Batch Dashboard...
              </div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-[hsl(var(--muted))] rounded-lg mb-8">
                {(['login', 'signup'] as Tab[]).map((t) => (
                  <button
                    key={`tab-${t}`}
                    onClick={() => setTab(t)}
                    className={`flex-1 py-2 text-sm font-500 rounded-md transition-all duration-150 ${
                      tab === t
                        ? 'bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm'
                        : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
                    }`}
                  >
                    {t === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
                ))}
              </div>

              {/* Login Form */}
              {tab === 'login' && (
                <form
                  onSubmit={loginForm.handleSubmit(onLogin)}
                  className="space-y-5 animate-fade-in"
                >
                  <div>
                    <label
                      htmlFor="login-email"
                      className="block text-xs font-500 text-[hsl(var(--muted-foreground))] mb-1.5"
                    >
                      Email Address
                    </label>
                    <input
                      id="login-email"
                      type="email"
                      placeholder="ops@auditgen.tech"
                      className={`w-full px-3.5 py-2.5 bg-[hsl(var(--muted))] border rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] transition-colors ${
                        loginForm.formState.errors.email
                          ? 'border-red-500/70' :'border-[hsl(var(--border))]'
                      }`}
                      {...loginForm.register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Enter a valid email address',
                        },
                      })}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="mt-1.5 text-xs text-red-400">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label
                        htmlFor="login-password"
                        className="text-xs font-500 text-[hsl(var(--muted-foreground))]"
                      >
                        Password
                      </label>
                      <button
                        type="button"
                        className="text-xs text-[hsl(var(--primary))] hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••••••"
                        className={`w-full px-3.5 py-2.5 pr-10 bg-[hsl(var(--muted))] border rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] transition-colors ${
                          loginForm.formState.errors.password
                            ? 'border-red-500/70' :'border-[hsl(var(--border))]'
                        }`}
                        {...loginForm.register('password', {
                          required: 'Password is required',
                          minLength: {
                            value: 8,
                            message: 'Minimum 8 characters',
                          },
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="mt-1.5 text-xs text-red-400">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 rounded border-[hsl(var(--border))] bg-[hsl(var(--muted))] accent-[hsl(var(--primary))]"
                      {...loginForm.register('remember')}
                    />
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      Remember this device for 30 days
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-600 rounded-lg hover:bg-[hsl(var(--primary)/0.9)] disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.98] duration-150"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        <span>Authenticating...</span>
                      </>
                    ) : (
                      <>
                        <span>Sign In to Console</span>
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Signup Form */}
              {tab === 'signup' && (
                <form
                  onSubmit={signupForm.handleSubmit(onSignup)}
                  className="space-y-5 animate-fade-in"
                >
                  <div>
                    <label
                      htmlFor="signup-name"
                      className="block text-xs font-500 text-[hsl(var(--muted-foreground))] mb-1.5"
                    >
                      Full Name
                    </label>
                    <input
                      id="signup-name"
                      type="text"
                      placeholder="Priya Mehta"
                      className={`w-full px-3.5 py-2.5 bg-[hsl(var(--muted))] border rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] transition-colors ${
                        signupForm.formState.errors.name
                          ? 'border-red-500/70' :'border-[hsl(var(--border))]'
                      }`}
                      {...signupForm.register('name', {
                        required: 'Full name is required',
                        minLength: { value: 2, message: 'Minimum 2 characters' },
                      })}
                    />
                    {signupForm.formState.errors.name && (
                      <p className="mt-1.5 text-xs text-red-400">
                        {signupForm.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="signup-email"
                      className="block text-xs font-500 text-[hsl(var(--muted-foreground))] mb-1.5"
                    >
                      Work Email
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      placeholder="you@yourcompany.io"
                      className={`w-full px-3.5 py-2.5 bg-[hsl(var(--muted))] border rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] transition-colors ${
                        signupForm.formState.errors.email
                          ? 'border-red-500/70' :'border-[hsl(var(--border))]'
                      }`}
                      {...signupForm.register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Enter a valid email',
                        },
                      })}
                    />
                    {signupForm.formState.errors.email && (
                      <p className="mt-1.5 text-xs text-red-400">
                        {signupForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="signup-password"
                      className="block text-xs font-500 text-[hsl(var(--muted-foreground))] mb-1.5"
                    >
                      Password
                    </label>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1.5">
                      Min 8 characters, at least one uppercase, number, and symbol
                    </p>
                    <div className="relative">
                      <input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••••••"
                        className="w-full px-3.5 py-2.5 pr-10 bg-[hsl(var(--muted))] border border-[hsl(var(--border))] rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] transition-colors"
                        {...signupForm.register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Minimum 8 characters' },
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.password && (
                      <p className="mt-1.5 text-xs text-red-400">
                        {signupForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="signup-confirm"
                      className="block text-xs font-500 text-[hsl(var(--muted-foreground))] mb-1.5"
                    >
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="signup-confirm"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="••••••••••••"
                        className={`w-full px-3.5 py-2.5 pr-10 bg-[hsl(var(--muted))] border rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] transition-colors ${
                          signupForm.formState.errors.confirmPassword
                            ? 'border-red-500/70' :'border-[hsl(var(--border))]'
                        }`}
                        {...signupForm.register('confirmPassword', {
                          required: 'Please confirm your password',
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                        aria-label="Toggle confirm password visibility"
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={15} />
                        ) : (
                          <Eye size={15} />
                        )}
                      </button>
                    </div>
                    {signupForm.formState.errors.confirmPassword && (
                      <p className="mt-1.5 text-xs text-red-400">
                        {signupForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 mt-0.5 rounded border-[hsl(var(--border))] bg-[hsl(var(--muted))] accent-[hsl(var(--primary))]"
                      {...signupForm.register('terms', {
                        required: 'You must accept the terms',
                      })}
                    />
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      I agree to the{' '}
                      <span className="text-[hsl(var(--primary))] hover:underline cursor-pointer">
                        Terms of Service
                      </span>{' '}
                      and{' '}
                      <span className="text-[hsl(var(--primary))] hover:underline cursor-pointer">
                        Privacy Policy
                      </span>
                    </span>
                  </label>
                  {signupForm.formState.errors.terms && (
                    <p className="text-xs text-red-400">
                      {signupForm.formState.errors.terms.message}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-600 rounded-lg hover:bg-[hsl(var(--primary)/0.9)] disabled:opacity-60 disabled:cursor-not-allowed transition-all active:scale-[0.98] duration-150"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        <span>Creating Account...</span>
                      </>
                    ) : (
                      <>
                        <span>Create Account</span>
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}